import moment from 'moment'
import { useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import * as Analytics from 'analytics/analytics'
import { getCheapestPackageForDuration } from '../Common/getCheapestPackageForDuration'
import config from 'constants/config'
import BaseOfferPriceContent from './BaseOfferPriceContent'

interface Props {
  duration?: number
  offer: App.Offer | App.OfferSummary
  flightOrigin?: string
  alternativeCheckIn?: string
  alternativeCheckOut?: string
  alternativePrice?: number
  alternativeMemberPrice?: number
}

const ALTERNATIVE_DATE_FORMAT = 'DD MMM'

function HotelPriceContent({
  duration,
  offer,
  flightOrigin,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
  alternativeMemberPrice,
}: Props) {
  const intl = useIntl()
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const showAlternativeDates = !!(alternativeCheckIn && alternativeCheckOut && (alternativeMemberPrice || alternativePrice))

  const { flightPrices, shouldDisplayValue } = offer
  const lowestPricePackage = useMemo<App.Package | null>(() => getCheapestPackageForDuration({
    enableDurationSearch,
    offer,
    duration,
  }), [offer, duration, enableDurationSearch])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      Analytics.trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'HotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  const flightPrice = flightOrigin ? flightPrices[flightOrigin] : undefined

  const packageCalculatedValues = useMemo(() => {
    const packageToDisplay = showAlternativeDates ? (offer.packages.find(p => p.price === alternativePrice) || lowestPricePackage) : lowestPricePackage
    if (!packageToDisplay) {
      return null
    }
    const { price, memberPrice, value } = packageToDisplay

    return {
      hotelOfferDisplayedValue: getHotelOfferDisplayedValue(!!offer.bundledWithFlightsOnly, (memberPrice || price), value, !!shouldDisplayValue, flightPrice),
      packageToDisplay,
    }
  }, [
    alternativePrice,
    flightPrice,
    lowestPricePackage,
    offer.bundledWithFlightsOnly,
    offer.packages,
    shouldDisplayValue,
    showAlternativeDates,
  ])

  if (!packageCalculatedValues) {
    return null
  }

  const {
    hotelOfferDisplayedValue: {
      highestDiscountPercent,
      shouldShowValue,
      shouldShowDiscountPercent,
    },
    packageToDisplay: {
      price,
      memberPrice,
      duration: durationToDisplay,
      value,
    },
  } = packageCalculatedValues

  const isDiscountPillHidden = offer.isDiscountPillHidden

  const captionText = showAlternativeDates ?
    `${moment(alternativeCheckIn).format(ALTERNATIVE_DATE_FORMAT)} - ${moment(alternativeCheckOut).format(ALTERNATIVE_DATE_FORMAT)} from` :
      intl.formatMessage(
        {
          id: 'pZBEP4O8YBHmO7LaOt6Z',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Duration in nights followed by "from" for hotel price caption',
        },
        { count: durationToDisplay },
      )
  const priceNumber = showAlternativeDates ? Math.ceil(alternativePrice ?? 0) : Math.ceil(price)
  const memberPriceNumber = showAlternativeDates ? Math.ceil(alternativeMemberPrice ?? 0) : Math.ceil(memberPrice)

  return <BaseOfferPriceContent
    offer={offer}
    duration={durationToDisplay}
    captionText={captionText}
    memberPrice={memberPriceNumber}
    price={priceNumber}
    value={value}
    discount={shouldShowDiscountPercent && !isDiscountPillHidden ? highestDiscountPercent : undefined}
    showValueDiscount={!showAlternativeDates && shouldShowValue}
    saleUnit={offer.saleUnit}
  />
}

export default HotelPriceContent
